import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

const TimerContext = createContext();

export const useTimer = () => useContext(TimerContext);

export const TimerProvider = ({ children }) => {
  
  const startTimeoutRef = useRef(null);
  
  // === Core State ===
  const [masterTime, setMasterTime] = useState(() =>
    Number(localStorage.getItem('masterTime')) || 30 * 60
  );

  const [penaltyTime, setPenaltyTime] = useState(() =>
    Number(localStorage.getItem('penaltyTime')) || 0
  );

  const [startTime, setStartTime] = useState(() =>
    Number(localStorage.getItem('startTime')) || null
  );
  const [isRunning, setIsRunning] = useState(
    localStorage.getItem('isRunning') === 'true'
  );

  const [elapsed, setElapsed] = useState(0);

  // === Ticking ===
  useEffect(() => {
    if (!isRunning || !startTime) return;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isRunning, startTime]);

  // === Game Control ===
  const initialiseGame = useCallback(() => {
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }

    const storedMaster = Number(localStorage.getItem('masterTime')) || 30 * 60;
    setMasterTime(storedMaster);
    localStorage.setItem('masterTime', storedMaster.toString());

    setPenaltyTime(0);
    localStorage.setItem('penaltyTime', '0');

    setStartTime(null);
    setElapsed(0);
    setIsRunning(false);
    localStorage.removeItem('startTime');
    localStorage.setItem('isRunning', 'false');
  }, []);

  // === Admin Control ===
  const updateMasterTime = useCallback((newSeconds) => {
    // clamp at 0 so we never go negative
    const safe = Math.max(0, Number(newSeconds));
    setMasterTime(safe);
    localStorage.setItem('masterTime', safe.toString());
  }, []);


  const startTimer = useCallback((delaySeconds = 0) => {
  if (isRunning) return;

  // Clear any pending delayed start
  if (startTimeoutRef.current) {
    clearTimeout(startTimeoutRef.current);
    startTimeoutRef.current = null;
  }

  const start = () => {
    const now = Date.now();
    setStartTime(now);
    localStorage.setItem('startTime', now.toString());
    setIsRunning(true);
    localStorage.setItem('isRunning', 'true');
  };

  if (delaySeconds > 0) {
    startTimeoutRef.current = setTimeout(() => {
      start();
      startTimeoutRef.current = null;
    }, delaySeconds * 1000);
  } else {
    start();
  }
}, [isRunning]);


const stopTimer = () => {
  if (startTimeoutRef.current) {
    clearTimeout(startTimeoutRef.current);
    startTimeoutRef.current = null;
  }

  setIsRunning(false);
  localStorage.setItem('isRunning', 'false');
};


  const addPenalty = (minutes = 1) => {
    setPenaltyTime(prev => {
      const next = prev + minutes * 60;
      localStorage.setItem('penaltyTime', next.toString());
      return next;
    });
  };

  // === Derived ===
  const timeRemaining = masterTime - elapsed - penaltyTime;
  const totalTime = masterTime - timeRemaining; // elapsed + penalty

  
  const formatTime = (totalSecs) => {
    const absSecs = Math.abs(totalSecs);
    const hours = Math.floor(absSecs / 3600).toString().padStart(2, '0');
    const mins = Math.floor((absSecs % 3600) / 60).toString().padStart(2, '0');
    const secs = (absSecs % 60).toString().padStart(2, '0');
    return `${totalSecs < 0 ? '-' : ''}${hours}:${mins}:${secs}`;
  };
  

  return (
<TimerContext.Provider value={{
  masterTime,
  penaltyTime,
  isRunning,
  initialiseGame,
  startTimer,
  stopTimer,
  addPenalty,
  updateMasterTime,
  timeRemaining,
  totalTime,
  formatTime,
}}>
      {children}
    </TimerContext.Provider>
  );
};